/**
 * @file content.schema.ts
 * @description Define o schema Zod para a coleção unificada 'content'.
 * Utiliza uma união discriminada por `type` para representar os diferentes tipos de conteúdo:
 * VIDEO (com subtipos COURSE | PODCAST | DOCUMENTARY | SERIES | LIVE), MAGAZINE,
 * PARTNER_CONTENT, PARTNER_FEED e MESSAGE_OF_DAY.
 *
 * Vídeos possuem estrutura interna polimórfica via `structure.kind`:
 *   - SECTIONED_COURSE  → módulos com aulas (subtype COURSE)
 *   - SECTIONED_SEASONS → temporadas com episódios (subtype DOCUMENTARY | SERIES)
 *   - FLAT_EPISODES     → lista plana de episódios (subtype PODCAST)
 *   - FLAT_BROADCASTS   → lista plana de transmissões com data (subtype LIVE)
 */

import { z } from 'zod';
import { ContentBlockSchema } from './content-block.schema';

// --- Enums ---

/**
 * Visibilidade do conteúdo
 * - VISIBLE: Conteúdo visível para usuários finais
 * - HIDDEN: Conteúdo escondido (apenas editores podem ver)
 */
export const ContentVisibilityEnum = z.enum(['VISIBLE', 'HIDDEN']);
export type ContentVisibility = z.infer<typeof ContentVisibilityEnum>;

/**
 * Subtipos de vídeo. Determina a forma da `structure` interna e o tratamento na UI.
 */
export const VideoSubtypeEnum = z.enum(['COURSE', 'PODCAST', 'DOCUMENTARY', 'SERIES', 'LIVE']);

/**
 * Para quem o conteúdo existe (A035).
 *
 * NÃO confundir com `targetAudience` (`MOTHER` | `FATHER_FIGURE`), que responde
 * outra coisa: quem DA CASA vai ler. Este responde que tipo de pessoa —
 * paciente ou profissional de saúde. Um curso de manejo clínico não é revista
 * para o pai; é formação para quem atende.
 *
 * Nasce `PACIENTE` por padrão: todo conteúdo que existe hoje é da paciente, e
 * documento antigo sem o campo continua lendo igual.
 */
export const PublicoDoConteudoEnum = z.enum(['PACIENTE', 'PROFISSIONAL']);
export type VideoSubtype = z.infer<typeof VideoSubtypeEnum>;

/**
 * De onde o item toca: arquivo enviado (GCS + Transcoder), YouTube ou
 * episódio do Spotify (A057). Um enum só, porque o schema do documento e as
 * duas cópias de entrada da API precisam aceitar as mesmas origens.
 */
export const VideoSourceEnum = z.enum(['UPLOAD', 'YOUTUBE', 'SPOTIFY']);
export type VideoSource = z.infer<typeof VideoSourceEnum>;

/**
 * Vocabulário ÚNICO de status de transcodificação.
 * O webhook do video-transcoder fala lowercase no fio (processing/completed/failed);
 * o mapeamento para este enum acontece em um único ponto
 * (CourseVideoService.updateTranscodeStatus). O preprocessor abaixo normaliza
 * valores legados gravados em lowercase antes da unificação.
 */
export const TranscodingStatusEnum = z.enum(['PENDING', 'TRANSCODING', 'READY', 'FAILED']);
export type TranscodingStatus = z.infer<typeof TranscodingStatusEnum>;

const LEGACY_TRANSCODING_STATUS_MAP: Record<string, TranscodingStatus> = {
  pending: 'PENDING',
  processing: 'TRANSCODING',
  transcoding: 'TRANSCODING',
  completed: 'READY',
  ready: 'READY',
  failed: 'FAILED',
};

// Backward-compatible: normaliza status legados em lowercase para o enum.
const backwardCompatibleTranscodingStatus = z.preprocess(
  (val) => (typeof val === 'string' && LEGACY_TRANSCODING_STATUS_MAP[val]) || val,
  TranscodingStatusEnum
);

/**
 * Audiência de conteúdo — fase da jornada (uso interno; resolução de perfil).
 * Mesmos valores usados em `TrailStage` (trail.schema.ts).
 */
export const AudienceStageEnum = z.enum(['PREGNANT', 'TENTANTE', 'POSTPARTUM', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE']);
export type AudienceStage = z.infer<typeof AudienceStageEnum>;

/**
 * Audiência de conteúdo — papel/identidade (uso interno; resolução de perfil).
 * Mesmos valores usados em `TrailRole`.
 */
export const AudienceRoleEnum = z.enum(['MOTHER', 'FATHER_FIGURE']);
export type AudienceRole = z.infer<typeof AudienceRoleEnum>;

/**
 * Perfis de audiência — 6 tuplas discretas (stage × role) representando todas
 * as combinações que fazem sentido no domínio. Usadas no filtro `targetAudience`
 * de vídeos.
 *
 * Por que tuplas em vez de cartesiano (stages + roles)?
 * Permite escolha precisa no admin: "Gestante" e "Fig paterna puérpera" pode
 * ser selecionado sem incluir acidentalmente "Gestante figura paterna" ou
 * "Puérpera mãe" (o que aconteceria com modelo cartesiano).
 */
export const AudienceProfileEnum = z.enum([
  'PREGNANT_MOTHER',     // Gestante
  'TENTANTE_MOTHER',     // Tentante
  'POSTPARTUM_MOTHER',   // Puérpera
  'MAE_DE_ANJO_MOTHER',  // Perdi meu bebê
  'ADOTANTE_MOTHER',     // Adotante
  'MAE_MOTHER',          // Mãe
  'PREGNANT_FATHER',     // Figura paterna — gestante
  'TENTANTE_FATHER',     // Figura paterna — tentante
  'POSTPARTUM_FATHER',   // Figura paterna — puérpera
  'MAE_DE_ANJO_FATHER',  // Figura paterna — perdi meu bebê
  'ADOTANTE_FATHER',     // Figura paterna — adotante
  'MAE_FATHER',          // Figura paterna — mãe
]);
export type AudienceProfile = z.infer<typeof AudienceProfileEnum>;

/**
 * Rótulo de cada perfil, para select, badge e mensagem de erro.
 *
 * Morava só no painel (`course.service.ts`); subiu para cá quando a API
 * passou a explicar conflito de mensagem do dia pelo nome da jornada.
 */
export const AUDIENCE_PROFILE_LABEL: Record<AudienceProfile, string> = {
  PREGNANT_MOTHER: 'Gestante',
  TENTANTE_MOTHER: 'Tentante',
  POSTPARTUM_MOTHER: 'Puérpera',
  MAE_DE_ANJO_MOTHER: 'Perdi meu bebê',
  ADOTANTE_MOTHER: 'Adotante',
  MAE_MOTHER: 'Mãe',
  PREGNANT_FATHER: 'Figura paterna — gestante',
  TENTANTE_FATHER: 'Figura paterna — tentante',
  POSTPARTUM_FATHER: 'Figura paterna — puérpera',
  MAE_DE_ANJO_FATHER: 'Figura paterna — perdi meu bebê',
  ADOTANTE_FATHER: 'Figura paterna — adotante',
  MAE_FATHER: 'Figura paterna — mãe',
};

/** Todos os perfis, na ordem dos selects. */
export const ALL_AUDIENCE_PROFILES: AudienceProfile[] = [...AudienceProfileEnum.options];

// --- Audiência ---

/**
 * Filtro de audiência: lista de perfis para os quais o vídeo é visível.
 * Vazio (ou ausente) = visível para qualquer perfil.
 *
 * Aceita formato legado `{ stages, roles }` via preprocess (calcula produto
 * cartesiano nos perfis correspondentes).
 */
const VideoTargetAudienceSchema = z.preprocess(
  (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object' && raw !== null) {
      const v = raw as { stages?: string[]; roles?: string[] };
      if (Array.isArray(v.stages) && Array.isArray(v.roles)) {
        const profiles: string[] = [];
        const suffixMap: Record<string, { mother: string; father: string }> = {
          PREGNANT:    { mother: 'PREGNANT_MOTHER',    father: 'PREGNANT_FATHER' },
          POSTPARTUM:  { mother: 'POSTPARTUM_MOTHER',  father: 'POSTPARTUM_FATHER' },
          TENTANTE:    { mother: 'TENTANTE_MOTHER',    father: 'TENTANTE_FATHER' },
          MAE_DE_ANJO: { mother: 'MAE_DE_ANJO_MOTHER', father: 'MAE_DE_ANJO_FATHER' },
          ADOTANTE:    { mother: 'ADOTANTE_MOTHER',    father: 'ADOTANTE_FATHER' },
          MAE:         { mother: 'MAE_MOTHER',         father: 'MAE_FATHER' },
        };
        for (const s of v.stages) {
          const mapping = suffixMap[s];
          if (!mapping) continue;
          for (const r of v.roles) {
            profiles.push(r === 'FATHER_FIGURE' ? mapping.father : mapping.mother);
          }
        }
        return profiles;
      }
    }
    return raw;
  },
  z.array(AudienceProfileEnum).default([])
);

// --- Itens de mídia (aula / episódio / transmissão) ---

/**
 * Unidade atômica reproduzível dentro de um vídeo.
 * Substitui o antigo `Video` (que representava uma aula).
 */
const MediaItemSchema = z.object({
  id: z.string().optional(), // gerado quando ausente (migração ou criação)
  title: z.string().optional(), // opcional para transmissões LIVE
  /** O texto da aula, abaixo do título no app (14/10/2026). Opcional: aula sem texto fica só com o título. */
  description: z.string().optional(),
  thumbnailUrl: z.string().url().or(z.literal('')),
  filename: z.string().nullable(),
  /**
   * Identidade estável do vídeo. Convenção: filename sem extensão (é o que o
   * transcoder recebe, o prefixo do path de output no bucket de serving e o
   * que o webhook devolve). Opcional apenas para docs pré-backfill.
   */
  videoId: z.string().optional(),
  index: z.number().int(),

  // Origem do vídeo
  videoSource: VideoSourceEnum.default('UPLOAD'),
  youtubeUrl: z.string().optional(),
  youtubeVideoId: z.string().optional(),
  /** Episódio do Spotify (A057): link canônico, id de 22 caracteres e se é videocast. */
  spotifyUrl: z.string().optional(),
  spotifyEpisodeId: z.string().optional(),
  spotifyVideo: z.boolean().optional(),

  // Transcodificação
  jobId: z.string().optional(),
  transcodingStatus: backwardCompatibleTranscodingStatus.optional(),
  transcodingJobId: z.string().optional(),
  transcodedUrls: z.object({
    '1080p': z.string().optional(),
    '720p': z.string().optional(),
    '480p': z.string().optional(),
    '360p': z.string().optional(),
  }).optional(),
  transcodingStartedAt: z.date().optional(),
  transcodingCompletedAt: z.date().optional(),
  transcodingFailedAt: z.date().optional(),
  transcodingError: z.string().optional(),
  durationSeconds: z.number().int().positive().optional(),

  // Campos específicos por subtipo
  /**
   * Apenas em FLAT_BROADCASTS (subtype LIVE).
   * Aceita string ISO, número (epoch ms), Firestore Timestamp `{_seconds,_nanoseconds}`
   * ou objeto com método `toDate()` — necessário para tolerar docs antigos persistidos
   * como string pelo admin antes do fix de coerção no input schema.
   */
  broadcastDate: z.preprocess((v) => {
    if (v === null || v === undefined || v === '') return undefined;
    if (v instanceof Date) return v;
    if (typeof v === 'string' || typeof v === 'number') {
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : d;
    }
    if (typeof v === 'object' && v !== null) {
      // Firestore Timestamp serializado (raw)
      const anyV = v as any;
      if (typeof anyV.toDate === 'function') return anyV.toDate();
      if (typeof anyV._seconds === 'number') {
        return new Date(anyV._seconds * 1000 + Math.floor((anyV._nanoseconds || 0) / 1e6));
      }
    }
    return v;
  }, z.date().optional()),
});

// --- Seções (módulos / temporadas) ---

/**
 * Agrupamento ordenado de itens de mídia. Usado em estruturas seccionadas
 * (SECTIONED_COURSE: módulos; SECTIONED_SEASONS: temporadas).
 * Substitui o antigo `Module`.
 */
const SectionSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  description: z.string(),
  coverUrl: z.string().url().or(z.literal('')),
  thumbnailUrl: z.string().url().or(z.literal('')).optional(),
  items: z.array(MediaItemSchema),
  index: z.number().int(),
  /** Lock por seção (usado apenas em SECTIONED_COURSE no contexto de barreiras). */
  isLocked: z.boolean().optional(),
});

// --- Estrutura interna polimórfica do vídeo ---

const SectionedCourseStructureSchema = z.object({
  kind: z.literal('SECTIONED_COURSE'),
  sections: z.array(SectionSchema),
});

const SectionedSeasonsStructureSchema = z.object({
  kind: z.literal('SECTIONED_SEASONS'),
  sections: z.array(SectionSchema),
});

const FlatEpisodesStructureSchema = z.object({
  kind: z.literal('FLAT_EPISODES'),
  items: z.array(MediaItemSchema),
});

const FlatBroadcastsStructureSchema = z.object({
  kind: z.literal('FLAT_BROADCASTS'),
  items: z.array(MediaItemSchema),
});

const VideoStructureSchema = z.discriminatedUnion('kind', [
  SectionedCourseStructureSchema,
  SectionedSeasonsStructureSchema,
  FlatEpisodesStructureSchema,
  FlatBroadcastsStructureSchema,
]);

/** Mapeamento canônico subtype → kind da estrutura. */
const SUBTYPE_TO_STRUCTURE_KIND: Record<VideoSubtype, VideoStructure['kind']> = {
  COURSE: 'SECTIONED_COURSE',
  DOCUMENTARY: 'SECTIONED_SEASONS',
  SERIES: 'SECTIONED_SEASONS',
  PODCAST: 'FLAT_EPISODES',
  LIVE: 'FLAT_BROADCASTS',
};

// --- Schemas das Entidades de Conteúdo Principais ---

/**
 * Vídeo é a entidade unificada que abrange Curso, Podcast, Documentário, Série e Live.
 * Substitui o antigo `Course`.
 */
const VideoSchema = z.object({
  id: z.string(),
  type: z.literal('VIDEO'),
  subtype: VideoSubtypeEnum,
  partnerId: z.string().optional(), // se preenchido, é exclusivo do parceiro
  /** Para quem este conteúdo existe. Ver `PublicoDoConteudoEnum` (A035). */
  publico: PublicoDoConteudoEnum.default('PACIENTE'),

  title: z.string(),
  description: z.string(),
  coverUrl: z.string().url().or(z.literal('')),
  /**
   * Imagem de destaque (450×470 px, aspect ratio fixo). Usada em áreas de
   * curadoria/destaque no admin e (futuramente) no mobile. Opcional —
   * ausente ou string vazia = sem imagem de destaque.
   */
  featuredImageUrl: z.string().url().or(z.literal('')).optional(),
  tags: z.array(z.string()).nullable(),

  /**
   * Autoria editorial (display-only). Lista de nomes livres exibidos na tela
   * de detalhe do vídeo no mobile (ex.: "Dra. Ana Silva", "Bruno Costa").
   * Não tem vínculo com a entidade User — é só string para créditos. Vazio ou
   * ausente = sem bloco de autoria.
   */
  authors: z.array(z.string()).default([]),

  /** Filtro de audiência (stages + roles). Ausente = visível para todos. */
  targetAudience: VideoTargetAudienceSchema.optional(),

  /** Estrutura interna polimórfica, consistente com `subtype`. */
  structure: VideoStructureSchema,

  visibility: ContentVisibilityEnum.default('HIDDEN'),
  isLocked: z.boolean().default(true), // bloqueado por padrão sob barreira
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
}).superRefine((data, ctx) => {
  const expectedKind = SUBTYPE_TO_STRUCTURE_KIND[data.subtype];
  if (data.structure.kind !== expectedKind) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Estrutura "${data.structure.kind}" incompatível com subtype "${data.subtype}" (esperado: "${expectedKind}").`,
      path: ['structure', 'kind'],
    });
  }
});

/**
 * @deprecated Schema legado mantido durante a janela de migração para permitir
 * leitura de documentos `type: 'COURSE'` ainda não migrados. Será removido após
 * a migração estar verificada em todos os ambientes.
 *
 * NÃO USE em código novo. Para o equivalente, use `VideoSchema` com `subtype: 'COURSE'`.
 */
const LegacyCourseSchema = z.object({
  id: z.string(),
  type: z.literal('COURSE'),
  partnerId: z.string().optional(),
  title: z.string(),
  coverUrl: z.string().url().or(z.literal('')),
  /** Imagem de destaque (450×470). Opcional em docs legados. */
  featuredImageUrl: z.string().url().or(z.literal('')).optional(),
  description: z.string(),
  tags: z.array(z.string()).nullable(),
  /**
   * Para quem este conteúdo existe. Ver `PublicoDoConteudoEnum` (A035).
   * Também no formato legado porque é por aqui que o admin ainda grava curso.
   */
  publico: PublicoDoConteudoEnum.default('PACIENTE'),
  modules: z.array(z.object({
    id: z.string().optional(),
    title: z.string(),
    coverUrl: z.string().url().or(z.literal('')),
    description: z.string(),
    videos: z.array(z.object({
      title: z.string(),
      description: z.string().optional(),
      thumbnailUrl: z.string().url().or(z.literal('')),
      filename: z.string().nullable(),
      /** Identidade estável (filename sem extensão) — mesma convenção do MediaItemSchema. */
      videoId: z.string().optional(),
      index: z.number().int(),
      videoSource: VideoSourceEnum.default('UPLOAD'),
      youtubeUrl: z.string().optional(),
      youtubeVideoId: z.string().optional(),
      spotifyUrl: z.string().optional(),
      spotifyEpisodeId: z.string().optional(),
      spotifyVideo: z.boolean().optional(),
      jobId: z.string().optional(),
      transcodingStatus: backwardCompatibleTranscodingStatus.optional(),
      transcodingJobId: z.string().optional(),
      transcodedUrls: z.object({
        '1080p': z.string().optional(),
        '720p': z.string().optional(),
        '480p': z.string().optional(),
        '360p': z.string().optional(),
      }).optional(),
      transcodingStartedAt: z.date().optional(),
      transcodingCompletedAt: z.date().optional(),
      transcodingFailedAt: z.date().optional(),
      transcodingError: z.string().optional(),
      durationSeconds: z.number().int().positive().optional(),
    })),
    index: z.number().int(),
    isLocked: z.boolean().optional(),
  })),
  /**
   * Filtro de audiência também em docs legados — sem isso, o ContentSchema.parse()
   * descarta o campo silenciosamente ao atualizar/criar um doc type:'COURSE',
   * fazendo com que a edição de audiência no admin pareça funcionar mas não persista.
   */
  targetAudience: VideoTargetAudienceSchema.optional(),
  /** Autoria editorial — mesmo conceito do VideoSchema. Opcional em docs legados. */
  authors: z.array(z.string()).default([]),
  visibility: ContentVisibilityEnum.default('HIDDEN'),
  isLocked: z.boolean().default(true),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

export const MagazineTypeEnum = z.enum(['GESTATIONAL', 'POSTPARTUM', 'TENTANTE', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE']);
export type MagazineType = z.infer<typeof MagazineTypeEnum>;

/**
 * Revistas identificadas por tópico: tudo que não é gestacional (por semana)
 * nem pós-parto (por faixa de semanas). As quatro jornadas têm a mesma forma
 * e por isso compartilham rotas, serviço e editor — `/magazines/{kind}/{topic}`.
 */
export const TOPIC_MAGAZINE_TYPES = ['TENTANTE', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE'] as const;
export type TopicMagazineType = (typeof TOPIC_MAGAZINE_TYPES)[number];
export type MagazineKind = 'tentante' | 'mae_de_anjo' | 'adotante' | 'mae';

/** Segmento de URL de cada tipo por tópico. */
export const MAGAZINE_KIND_BY_TYPE: Record<TopicMagazineType, MagazineKind> = {
  TENTANTE: 'tentante',
  MAE_DE_ANJO: 'mae_de_anjo',
  ADOTANTE: 'adotante',
  MAE: 'mae',
};
export const MAGAZINE_TYPE_BY_KIND: Record<MagazineKind, TopicMagazineType> = {
  tentante: 'TENTANTE',
  mae_de_anjo: 'MAE_DE_ANJO',
  adotante: 'ADOTANTE',
  mae: 'MAE',
};

/** Como cada tipo aparece em frase: "revista tentante", "revista perdi meu bebê". */
export const MAGAZINE_TYPE_LABEL: Record<MagazineType, string> = {
  GESTATIONAL: 'gestacional',
  POSTPARTUM: 'pós-parto',
  TENTANTE: 'tentante',
  MAE_DE_ANJO: 'perdi meu bebê',
  ADOTANTE: 'adotante',
  MAE: 'mãe',
};

export function isTopicMagazineType(value: unknown): value is TopicMagazineType {
  return (TOPIC_MAGAZINE_TYPES as readonly string[]).includes(String(value));
}
export const TargetAudienceEnum = z.enum(['MOTHER', 'FATHER_FIGURE']);

// Backward-compatible preprocessor: maps legacy values to valid enum values
// so data written before migration (FATHER_FIGURE, PARTNER) still parses correctly.
const backwardCompatibleMagazineType = z.preprocess(
  (val) => {
    if (val === 'FATHER_FIGURE' || val === 'PARTNER') return 'GESTATIONAL';
    return val;
  },
  MagazineTypeEnum
).default('GESTATIONAL');

const MagazineSchema = z.object({
  id: z.string(),
  type: z.literal('MAGAZINE'),
  partnerId: z.string().optional(),
  coverUrl: z.string().url().or(z.literal('')),
  magazineType: backwardCompatibleMagazineType,
  targetAudience: TargetAudienceEnum.default('MOTHER'),
  /**
   * A revista da mãe vale também para a figura paterna da mesma jornada
   * (A058). Só tem sentido na revista da mãe; a regra de quem lê o quê está em
   * `revista-por-publico.ts`. Opcional: nenhuma revista gravada tem o campo, e
   * ausente é "não compartilhada".
   */
  sharedWithFatherFigure: z.boolean().optional(),
  gestationalWeek: z.number().int().min(1).max(42).optional(),
  topic: z.string().optional(),
  title: z.string().min(1).max(120).optional(),
  postpartumStartWeek: z.number().int().min(0).max(52).optional(),
  postpartumEndWeek: z.number().int().min(0).max(52).optional(),
  /**
   * A posição na lista das revistas temáticas (tentante, perdi meu bebê,
   * adotante, mãe), que não têm período (10/10/2026). Gestante segue a
   * semana e pós-parto, a faixa; nelas o campo é ignorado. Opcional: sem
   * ordem, a revista vem depois das ordenadas, pela última edição.
   */
  ordem: z.number().int().min(1).max(999).nullable().optional(),
  teaser: z.string(),
  /**
   * Palavras-chave da busca, o mesmo campo que o vídeo já tinha.
   *
   * Nasce opcional, e não `nullable()` como no vídeo, porque toda revista já
   * gravada está sem ele: exigir agora derrubaria a leitura de todas elas.
   */
  tags: z.array(z.string()).nullable().optional(),
  headerImageUrl: z.string().url().or(z.literal('')),
  blocks: z.array(ContentBlockSchema),
  currentVersion: z.number().int().positive().default(1),
  totalRevisions: z.number().int().positive().default(1),
  lastRevisionAt: z.date().optional(),
  lastAuthorId: z.string().optional(),
  visibility: ContentVisibilityEnum.default('VISIBLE'),
  isLocked: z.boolean().default(true), // Content is locked by default if barrier is active
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
}).superRefine((data, ctx) => {
  // GESTATIONAL requires gestationalWeek, UNLESS it has topic (legacy FATHER_FIGURE data or FF audience)
  if (data.magazineType === 'GESTATIONAL' && !data.gestationalWeek && !data.topic) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'gestationalWeek é obrigatório para revistas gestacionais',
      path: ['gestationalWeek'],
    });
  }
  // Non-gestational types always require topic; GESTATIONAL FF also requires topic
  const topicRequiredTypes: string[] = ['POSTPARTUM', 'TENTANTE', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE'];
  const requiresTopic = topicRequiredTypes.includes(data.magazineType) ||
    (data.magazineType === 'GESTATIONAL' && data.targetAudience === 'FATHER_FIGURE' && !data.gestationalWeek);
  if (requiresTopic && !data.topic) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'topic é obrigatório para revistas pós-parto, tentante e figura paterna',
      path: ['topic'],
    });
  }
  // Validate postpartum week range: endWeek must be >= startWeek
  if (data.magazineType === 'POSTPARTUM') {
    if (data.postpartumStartWeek !== undefined && data.postpartumEndWeek !== undefined) {
      if (data.postpartumEndWeek < data.postpartumStartWeek) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Semana final deve ser maior ou igual à semana inicial',
          path: ['postpartumEndWeek'],
        });
      }
    }
  }
});

const PartnerContentSchema = z.object({
  id: z.string(),
  type: z.literal('PARTNER_CONTENT'),
  partnerId: z.string(),
  coverUrl: z.string().url(),
  blocks: z.array(ContentBlockSchema),
  visibility: ContentVisibilityEnum.default('VISIBLE'),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

/**
 * Anexo de um post do feed (A060): imagem, PDF ou vídeo.
 *
 * O vídeo não é um arquivo pronto como a imagem: sobe direto ao bucket, é
 * transcodificado e só então toca. Por isso carrega `filename`/`videoId`
 * (a identidade que o transcoder devolve) e `transcodingStatus`, e a `url`
 * pode ficar vazia — o app resolve o stream pelo `filename`, como nas aulas.
 * Exportado para a API usar o mesmo schema na entrada, em vez de cópias.
 */
export const PartnerFeedAttachmentTypeEnum = z.enum(['IMAGE', 'PDF', 'VIDEO']);
export const PartnerFeedAttachmentSchema = z.object({
  type: PartnerFeedAttachmentTypeEnum,
  url: z.string().url().or(z.literal('')),
  name: z.string().optional(),
  // Só vídeo
  filename: z.string().optional(),
  videoId: z.string().optional(),
  transcodingStatus: TranscodingStatusEnum.optional(),
  transcodedUrls: z.object({
    '1080p': z.string().optional(),
    '720p': z.string().optional(),
    '480p': z.string().optional(),
    '360p': z.string().optional(),
  }).optional(),
  transcodingError: z.string().optional(),
  /** Vertical é o formato do feed; fica gravado para o app reservar a caixa certa. */
  orientation: z.enum(['VERTICAL', 'HORIZONTAL']).optional(),
  durationSeconds: z.number().int().positive().optional(),
}).superRefine((a, ctx) => {
  if (a.type !== 'VIDEO' && !a.url) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['url'], message: 'Imagem e PDF precisam de url' });
  }
  if (a.type === 'VIDEO' && !a.filename) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['filename'], message: 'Vídeo precisa de filename' });
  }
});
export type PartnerFeedAttachment = z.infer<typeof PartnerFeedAttachmentSchema>;

const PartnerFeedItemSchema = z.object({
  id: z.string(),
  type: z.literal('PARTNER_FEED'),
  partnerId: z.string(),
  content: z.string(),
  attachments: z.array(PartnerFeedAttachmentSchema).default([]),
  /** Vazio ou ausente mantém os posts antigos visíveis para todas as jornadas. */
  targetAudience: z.array(AudienceProfileEnum).optional(),
  /** Empresas da própria marca. Com jornada, os dois recortes devem ser atendidos. */
  companyIds: z.array(z.string().min(1)).optional(),
  publishedAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

export const PartnerFeedItemInputSchema = PartnerFeedItemSchema.omit({
  id: true,
  publishedAt: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});

const MessageOfDaySchema = z.object({
  id: z.string(),
  type: z.literal('MESSAGE_OF_DAY'),
  partnerId: z.string(),
  message: z.string().min(1).max(500),
  scheduledDate: z.string(), // formato 'YYYY-MM-DD'
  /**
   * Para quem é a mensagem: os mesmos perfis de jornada do vídeo e da
   * trilha. Vazio ou ausente = todas as jornadas, que é o que toda mensagem
   * gravada antes deste campo significa — por isso nasce opcional e não há
   * migração. A regra de escolha e de conflito está em `mensagem-do-dia.ts`.
   */
  targetAudience: z.array(AudienceProfileEnum).optional(),
  /**
   * Empresas da carteira da marca cujas colaboradoras recebem a mensagem
   * (A056). Vazio ou ausente = todas, com ou sem empresa. Combina com
   * `targetAudience`: as duas juntas são "gestantes da Empresa X".
   */
  companyIds: z.array(z.string().min(1)).optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

export const MessageOfDayInputSchema = MessageOfDaySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});


// --- Schema Final e Unificado ---

/**
 * União de todos os tipos de conteúdo persistidos na coleção `content`.
 *
 * Inclui `LegacyCourseSchema` (type: 'COURSE') apenas durante a janela de migração:
 * permite que o backend leia documentos ainda no formato antigo enquanto a migração
 * é executada. Após verificação em prod, remover `LegacyCourseSchema`.
 */
const ContentSchema = z.union([
  VideoSchema,
  LegacyCourseSchema,
  MagazineSchema,
  PartnerContentSchema,
  PartnerFeedItemSchema,
  MessageOfDaySchema,
]);


// --- Inferred TypeScript Types ---

type Content = z.infer<typeof ContentSchema>;
type Video = z.infer<typeof VideoSchema>;
type VideoStructure = z.infer<typeof VideoStructureSchema>;
type MediaItem = z.infer<typeof MediaItemSchema>;
type Section = z.infer<typeof SectionSchema>;
type VideoTargetAudience = z.infer<typeof VideoTargetAudienceSchema>; // AudienceProfile[]

/** @deprecated Use `Video` com `subtype: 'COURSE'`. Mantido para leitura legada. */
type LegacyCourse = z.infer<typeof LegacyCourseSchema>;

type Magazine = z.infer<typeof MagazineSchema>;
type PartnerContent = z.infer<typeof PartnerContentSchema>;
type PartnerFeedItem = z.infer<typeof PartnerFeedItemSchema>;
type PartnerFeedItemInput = z.infer<typeof PartnerFeedItemInputSchema>;
type MessageOfDay = z.infer<typeof MessageOfDaySchema>;
type MessageOfDayInput = z.infer<typeof MessageOfDayInputSchema>;

// Helper types for magazine discrimination
type GestationalMagazine = Magazine & { magazineType: 'GESTATIONAL'; gestationalWeek: number };
type PostpartumMagazine = Magazine & { magazineType: 'POSTPARTUM'; topic: string; postpartumStartWeek?: number; postpartumEndWeek?: number };
type TentanteMagazine = Magazine & { magazineType: 'TENTANTE'; topic: string };
type TargetAudience = z.infer<typeof TargetAudienceEnum>;


// --- Deprecated aliases (janela de migração) ---

/**
 * @deprecated Use `VideoSchema` com `subtype: 'COURSE'`. Este alias aponta para o
 * schema legado e existe apenas para destravar a compilação do código backend que
 * ainda não foi totalmente migrado. NÃO USE em código novo.
 */
const CourseSchema = LegacyCourseSchema;

/**
 * @deprecated Use `SectionSchema`. Este alias é o schema legado de módulos
 * (com `videos[]` interno). Existe para manter validação de inputs legados de
 * admin (que envia `modules[]`) durante a janela de migração.
 */
const ModuleSchema = LegacyCourseSchema.shape.modules.element;

type Course = LegacyCourse;
type Module = z.infer<typeof ModuleSchema>;

// --- Public Exports ---

export {
  ContentSchema,
  VideoSchema,
  VideoStructureSchema,
  SectionSchema,
  MediaItemSchema,
  VideoTargetAudienceSchema,
  LegacyCourseSchema,
  CourseSchema,
  ModuleSchema,
  MagazineSchema,
  PartnerContentSchema,
  PartnerFeedItemSchema,
  MessageOfDaySchema,
  SUBTYPE_TO_STRUCTURE_KIND,
};
export type {
  Content,
  Video,
  VideoStructure,
  Section,
  MediaItem,
  VideoTargetAudience,
  LegacyCourse,
  Course,
  Module,
  Magazine,
  GestationalMagazine,
  PostpartumMagazine,
  TentanteMagazine,
  TargetAudience,
  PartnerContent,
  PartnerFeedItem,
  PartnerFeedItemInput,
  MessageOfDay,
  MessageOfDayInput,
};
